import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import useUserStore from "../stores/useUserStore";
import { apiFetchAuthed } from "../apiHelpers";
import Login from "./Login";
import LoadingIcon from "../LoadingIcon/LoadingIcon";
import { OAuthRequestInfo, scopeLabel } from "./oauthTypes";
import { buttonClass } from "../components/ui/Button";
import { Alert } from "../components/ui/Alert";
import Reveal from "../components/ui/Reveal";

type Load =
	| { state: "loading" }
	| { state: "expired" }
	| { state: "error"; message: string }
	| { state: "ready"; request: OAuthRequestInfo };

type Decision = "idle" | "approving" | "denying";

/**
 * /oauth/consent?request=<id>: where a bot (Grok, Cursor) asks to act as the
 * signed-in user. Sits outside the app shell like /pilot-auth, so it checks the
 * session itself and reuses <Login> inline.
 */
const minutesAgo = (iso: string) => {
	const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
	return m === 0 ? "just now" : m === 1 ? "1 minute ago" : `${m} minutes ago`;
};

// A stale stored session reads as logged in until a call fails: drop it so <Login> shows.
const signOut = () => useUserStore.getState().logout();

const OAuthConsent = () => {
	const { search } = useLocation();
	const user = useUserStore((s) => s.user);
	const isInitializing = useUserStore((s) => s.isInitializing);
	const requestId = useMemo(() => new URLSearchParams(search).get("request") || "", [search]);

	const [load, setLoad] = useState<Load>({ state: "loading" });
	const [decision, setDecision] = useState<Decision>("idle");
	const [errorMsg, setErrorMsg] = useState<string | null>(null);

	const loggedIn = !!user.loggedin;

	useEffect(() => {
		if (!requestId || isInitializing || !loggedIn) return;
		let cancelled = false;
		setLoad({ state: "loading" });
		apiFetchAuthed(`/oauth/requests/${encodeURIComponent(requestId)}`, { silent: true, onUnauthorized: signOut })
			.then((request: OAuthRequestInfo) => {
				if (!cancelled) setLoad({ state: "ready", request });
			})
			.catch((err: { status?: number; message?: string }) => {
				if (cancelled) return;
				if (err?.status === 404) setLoad({ state: "expired" });
				else setLoad({ state: "error", message: err?.message || "Could not load this request." });
			});
		return () => {
			cancelled = true;
		};
	}, [requestId, isInitializing, loggedIn]);

	const decide = useCallback(
		async (kind: "approve" | "deny") => {
			setDecision(kind === "approve" ? "approving" : "denying");
			setErrorMsg(null);
			try {
				const { redirect } = await apiFetchAuthed(
					`/oauth/requests/${encodeURIComponent(requestId)}/${kind}`,
					{ method: "POST", silent: true, onUnauthorized: signOut }
				);
				window.location.assign(redirect);
			} catch (err) {
				setDecision("idle");
				const e = err as { status?: number; message?: string };
				setErrorMsg(
					e?.status === 404
						? "This request expired. Start again from the bot."
						: e?.message || "Something went wrong. Try again, or start again from the bot."
				);
			}
		},
		[requestId]
	);

	// Never inside another site's frame: Approve must not be clickjackable.
	// vercel.json also sends frame-ancestors 'none' for this path.
	if (window.top !== window.self) {
		return (
			<Shell title="Bot sign-in" tone="danger">
				<p className="text-sm text-red-300">This page cannot be shown inside another site.</p>
				<p className="mt-2 text-sm text-on-surface">Open the sign-in link in its own browser tab.</p>
			</Shell>
		);
	}

	if (!requestId) {
		return (
			<Shell title="Bot sign-in" tone="danger">
				<p className="text-sm text-red-300">This link is missing its request.</p>
				<p className="mt-2 text-sm text-on-surface">Start again from the bot to get a fresh link.</p>
			</Shell>
		);
	}

	if (isInitializing) {
		return (
			<Shell title="Bot sign-in">
				<LoadingIcon />
				<p className="mt-2 text-center text-sm text-on-surface-variant">Checking your session…</p>
			</Shell>
		);
	}

	if (!loggedIn) {
		return (
			<div className="min-h-screen bg-[#191919] pt-10 pb-16" style={{ fontFamily: "RobotoMono, sans-serif" }}>
				<div className="w-11/12 max-w-md mx-auto text-center text-white">
					<h3 className="text-lg font-bold">A bot wants to connect</h3>
					<p className="text-sm text-on-surface-variant mt-1 mb-4">Sign in to continue.</p>
				</div>
				<Login onAuthenticated={() => { /* the store update re-renders into the consent step */ }} />
			</div>
		);
	}

	if (load.state === "loading") {
		return (
			<Shell title="Bot sign-in">
				<LoadingIcon />
				<p className="mt-2 text-center text-sm text-on-surface-variant">Loading the request…</p>
			</Shell>
		);
	}

	if (load.state === "expired") {
		return (
			<Shell title="This request expired" tone="danger">
				<p className="text-sm text-on-surface">Start again from the bot to get a fresh link. You can close this window.</p>
			</Shell>
		);
	}

	if (load.state === "error") {
		return (
			<Shell title="Could not load this request" tone="danger">
				<Alert variant="text">{load.message}</Alert>
				<p className="mt-2 text-sm text-on-surface">Reload the page, or start again from the bot.</p>
			</Shell>
		);
	}

	const { request } = load;

	if (!request.can_connect) {
		return (
			<Shell title={`${request.client_name} can't connect yet`}>
				<p className="text-sm text-on-surface">
					Your account <span className="font-semibold text-white">{user.email}</span> doesn't have the
					"Connect a bot" permission. Ask an admin to turn it on for you, then start again from the bot.
				</p>
			</Shell>
		);
	}

	const busy = decision !== "idle";

	return (
		<Shell title={`${request.client_name} wants to act as you in Firefleet`}>
			<p className="mb-2 text-sm text-on-surface">
				Signed in as <span className="font-semibold text-white">{user.email}</span>.
			</p>
			<p className="mb-4 text-sm text-on-surface">
				Requested {minutesAgo(request.requested_at)}. Only approve if you just pressed Connect in your
				own bot. If someone sent you this link, deny it.
			</p>

			<div className="px-3 py-2 rounded border border-[#464646] bg-[#3a3a3a] text-sm break-all">
				<span className="block text-xs text-on-surface-variant mb-1">Sends you back to</span>
				<code className="text-on-surface">{request.callback_host}</code>
			</div>

			<p className="mt-5 mb-2 text-sm font-semibold text-white">It will be able to</p>
			<ul className="space-y-1.5 text-sm text-on-surface">
				{request.scopes.map((scope) => (
					<li key={scope} className="flex items-start gap-2">
						<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0">
							<path d="M9.55 18 3.85 12.3l1.4-1.4 4.3 4.3 9.2-9.2 1.4 1.4z" fill="#ffcc33" />
						</svg>
						<span>{scopeLabel(scope)}</span>
					</li>
				))}
			</ul>

			<p className="mt-4 text-xs text-on-surface-variant">
				It sees only what your account can see. You can disconnect it any time under your profile.
			</p>

			<Reveal show={!!errorMsg}>
				<Alert className="mt-4">{errorMsg}</Alert>
			</Reveal>

			<button
				type="button"
				onClick={() => decide("approve")}
				disabled={busy}
				className={`${buttonClass("filled", "lg")} mt-5 w-full`}
			>
				{decision === "approving" ? "Connecting…" : `Approve ${request.client_name}`}
			</button>
			<button
				type="button"
				onClick={() => decide("deny")}
				disabled={busy}
				className={`${buttonClass("quiet", "lg")} mt-2 w-full`}
			>
				{decision === "denying" ? "Declining…" : "Deny"}
			</button>
		</Shell>
	);
};

const Shell = ({
	title,
	tone,
	children,
}: {
	title: string;
	tone?: "danger";
	children: React.ReactNode;
}) => (
	<div
		className="flex items-center justify-center min-h-screen px-4 bg-[#191919]"
		style={{ fontFamily: "RobotoMono, sans-serif" }}
	>
		<div
			className={`w-11/12 max-w-md p-6 bg-[#2a2a2a] text-white rounded-lg shadow-lg border-2 ${
				tone === "danger" ? "border-red-800" : "border-black"
			}`}
		>
			<h3 className="text-center text-lg font-bold mb-4 text-balance">{title}</h3>
			{children}
		</div>
	</div>
);

export default OAuthConsent;
